import "./PageTacticAnalysis.css";
import BoxLogin from "shared/components/box/BoxLogin.js";
import ButtonLogin from "shared/components/buttons/ButtonLogin.js";
import React, { useEffect, useMemo, useState } from "react";
import { getMatchReport, getMatch } from "shared/services/api-mfl.js";
import { getMatchDossiers, moveMatchDossier } from "shared/services/api-assistant.js";
import ControllerMatchDossier, { ControllerMatchDossierAddButton } from "shared/components/controllers/ControllerMatchDossier.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import "./../bars/MenuTop.css";
import BoxCard from "shared/components/box/BoxCard.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import ItemRowPlayer from "shared/components/items/ItemRowPlayer.js";
import ItemRowClubMfl from "shared/components/items/ItemRowClubMfl.js";

const PageToolsTacticAnalysis = (props) => {
  const [dossiers, setDossiers] = useState(null);
  const [selectedDossierId, setSelectedDossierId] = useState(null);
  const [selectedDossierId2, setSelectedDossierId2] = useState(null);

  const [loadedMatches, setLoadedMatches] = useState({});
  const [loadedMatchReports, setLoadedMatchReports] = useState({});
  const [matchesToLoad, setMatchesToLoad] = useState([]);
  const isLoading = matchesToLoad?.length > 0;

  const [currentAggregatedReport, setCurrentAggregatedReport] = useState({
    players: {},
  });

  const [view, setView] = useState("overview");
  const [aggregationMethod, setAggregationMethod] = useState("per90");
  const [playerView, setPlayerView] = useState("attack");
  const [selectedCells, setSelectedCells] = useState([]);
  const [minGames, setMinGames] = useState(0);
  const [minMinutes, setMinMinutes] = useState(0);

  const [sortConfig, setSortConfig] = useState({
    key: "games",
    direction: "desc",
  });

  const formatStat = (value, digits = 2, suffix = "") => {
    if (value === 0 || value === undefined || value === null || Number.isNaN(value)) {
      return "--";
    }

    return `${Number(value).toFixed(digits)}${suffix}`;
  };

  const formatPercentage = (numerator, denominator, digits = 0) => {
    if (!denominator || denominator === 0) {
      return "--";
    }

    return `${((numerator / denominator) * 100).toFixed(digits)}%`;
  };

  const fetchMatchDossiers = (triggerLoading = true) => {
    if (props.assistantUser) {
      if (triggerLoading) {
        setDossiers(null);
      }

      getMatchDossiers({
        handleSuccess: (d) => {
          setDossiers(d.data.getMatchDossiers);
        },
        handleError: (e) => console.log(e),
      });
    }
  };

  const fetchMatchReports = () => {
    if (!matchesToLoad?.length) {
      return;
    }

    const id = matchesToLoad[0];

    if (loadedMatchReports[id]) {
      setMatchesToLoad((prev) => prev.slice(1));
      return;
    }

    getMatchReport({
      id,
      handleSuccess: (m) => {
        setLoadedMatchReports((prev) => ({
          ...prev,
          [id]: m,
        }));

        getMatch({
          id,
          handleSuccess: (c) => {
            setMatchesToLoad((prev) => prev.slice(1));

            setLoadedMatches((prev) => ({
              ...prev,
              [id]: c,
            }));
          },
          handleError: (e) => console.log(e),
        });
      },
      handleError: (e) => console.log(e),
    });
  };

  const changeDossierPosition = (id, position) => {
    moveMatchDossier({
      handleSuccess: () => {
        fetchMatchDossiers(false);
      },
      params: {
        id,
        position,
      },
    });
  };

  const addStats = (current, added) => {
    const currentMinutes = current.minutes || 0;
    const addedMinutes = (added.time || 0) / 60;
    const totalMinutes = currentMinutes + addedMinutes;

    const aggregated = {
      games: (current.games || 0) + 1,
      minutes: totalMinutes,
    };

    Object.keys(added).forEach((key) => {
      if (
        typeof added[key] !== "number" ||
        key === "games" ||
        key === "minutes" ||
        key === "playerId" ||
        key === "time"
      ) {
        return;
      }

      if (aggregationMethod === "per90") {
        // Current value is already normalized per 90.
        // Convert it back to the accumulated raw value.
        const currentPer90 = current[key] || 0;
        const currentRaw = currentPer90 * (currentMinutes / 90);

        // Added value is raw (from the individual match).
        const totalRaw = currentRaw + added[key];

        // Normalize the accumulated value back to per 90.
        aggregated[key] = totalMinutes > 0 ? (totalRaw * 90) / totalMinutes : 0;
      } else {
        // Total/raw aggregation.
        aggregated[key] = (current[key] || 0) + added[key];
      }
    });

    return aggregated;
  };

  const renderTeamsTable = (teamsData, statKeys, sortKey, labels) => {
    // Convert the teams object into an array of { clubId, dossierId, stats, club }
    const teamsArray = Object.entries(teamsData || {}).map(([key, teamData]) => {
      const [clubId, dossierId] = key.split("-");
      return {
        key, // Unique key for React rendering
        clubId,
        dossierId,
        ...teamData,
      };
    });

    // Sort teams by the specified stat key
    const sortedTeams = teamsArray.sort((a, b) => {
      const aValue = a.stats?.[sortKey] || 0;
      const bValue = b.stats?.[sortKey] || 0;
      return bValue - aValue; // Descending order
    });

    return (
      <div className="d-flex flex-column flex-fill mt-2">
        <div className="d-flex flex-row">
          <div className="flex-grow-1 text-truncate"></div>
          {/* Render stat headers */}
          {statKeys.map((key, i) => (
            <div
              key={key}
              className="d-flex justify-content-center d-none d-sm-flex"
              style={{ minWidth: 60, cursor: "default" }}
            >
              {labels?.[i] || key}
            </div>
          ))}
        </div>

        {sortedTeams.map((team) => {
          const isFromDossier2 = team.dossierId === selectedDossierId2;
          return (
            <div
              key={team.key} // Use the composite key for React's key prop
              className={
                "d-flex flex-row table-row " +
                (isFromDossier2 ? "light-main-blue-color-background" : "light-main-pink-color-background")
              }
            >
              <div className="flex-grow-1 text-truncate">
                <ItemRowClubMfl c={team.club} />
              </div>
              {statKeys.map((key) => (
                <div
                  key={`${team.key}-${key}`}
                  className="d-flex justify-content-center d-none d-sm-flex table-cell"
                  style={{ minWidth: 60, cursor: "default" }}
                >
                  {formatStat(team.stats?.[key])}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const computeAggregatedReport = (dossierId = selectedDossierId) => {
    const dossier = dossiers?.filter((d) => d.id === dossierId).pop();

    if (!dossier?.matches) {
      return;
    }

    const r = {
      teams: {},
      players: {},
    };

    dossier.matches.forEach((id) => {
      const match = loadedMatches[id];
      const matchReport = loadedMatchReports[id];

      if (!match || !matchReport) {
        return;
      }

      // --- Helper: Aggregate team stats for a match ---
      const aggregateTeamStats = (teamId, playersStats, matchSide, isHome, matchResult) => {
        // Use a composite key: `${teamId}-${dossierId}`
        const teamKey = `${teamId}-${dossierId}`;

        if (!r.teams[teamKey]) {
          r.teams[teamKey] = {
            stats: {},
            players: [],
            club: matchSide.club,
            dossierId, // Track which dossier this team belongs to
            clubId: teamId, // Store the original clubId for rendering
          };
          // Initialize all possible stats to 0
          r.teams[teamKey].stats.games = 0;
          r.teams[teamKey].stats.wins = 0;
          r.teams[teamKey].stats.draws = 0;
          r.teams[teamKey].stats.losses = 0;
        }

        // Rest of the logic remains the same, but use `teamKey` instead of `teamId`
        const matchTeamStats = {};
        playersStats.forEach((p) => {
          Object.keys(p).forEach((key) => {
            if (typeof p[key] === "number" && key !== "playerId" && key !== "time") {
              matchTeamStats[key] = (matchTeamStats[key] || 0) + p[key];
            }
          });
          r.teams[teamKey].players.push(p.playerId);
        });

        // Update team stats
        if (aggregationMethod === "per90") {
          Object.keys(matchTeamStats).forEach((key) => {
            const currentValue = r.teams[teamKey].stats[key] || 0;
            const currentMatches = r.teams[teamKey].stats.games || 0;
            r.teams[teamKey].stats[key] = (currentValue * currentMatches + matchTeamStats[key]) / (currentMatches + 1);
          });
        } else {
          Object.keys(matchTeamStats).forEach((key) => {
            r.teams[teamKey].stats[key] = (r.teams[teamKey].stats[key] || 0) + matchTeamStats[key];
          });
        }
        r.teams[teamKey].stats.games += 1;

        // Update wins, draws, losses
        if (matchResult === "win") {
          r.teams[teamKey].stats.wins += 1;
        } else if (matchResult === "draw") {
          r.teams[teamKey].stats.draws += 1;
        } else if (matchResult === "loss") {
          r.teams[teamKey].stats.losses += 1;
        }
      };

      // --- Determine match result for home and away ---
      const homeScore = match.homeScore || 0;
      const awayScore = match.awayScore || 0;
      let homeResult, awayResult;
      if (homeScore > awayScore) {
        homeResult = "win";
        awayResult = "loss";
      } else if (homeScore < awayScore) {
        homeResult = "loss";
        awayResult = "win";
      } else {
        homeResult = "draw";
        awayResult = "draw";
      }

      // Home Team Stats Aggregation
      if (matchReport.home?.playersStats) {
        const homeTeamId = match.homeSquad.club.id;
        aggregateTeamStats(homeTeamId, matchReport.home.playersStats, match.homeSquad, true, homeResult);
      }

      // Away Team Stats Aggregation
      if (matchReport.away?.playersStats) {
        const awayTeamId = match.awaySquad.club.id;
        aggregateTeamStats(awayTeamId, matchReport.away.playersStats, match.awaySquad, false, awayResult);
      }

      // --- Player aggregation logic ---
      matchReport?.home?.playersStats?.forEach((p) => {
        const playerKey = `${p.playerId}-${dossierId}`; // Composite key for players
        if (r.players[playerKey]) {
          r.players[playerKey]["stats"] = addStats(r.players[playerKey]["stats"], p);
        } else {
          r.players[playerKey] = {};
          r.players[playerKey]["player"] = match.homeFormation.positions
            ?.map((c) => c.player)
            .filter((c) => c.id === p.playerId)
            .pop();
          r.players[playerKey]["stats"] = addStats(
            {
              games: 0,
              minutes: 0,
            },
            p,
          );
          r.players[playerKey]["clubId"] = match.homeSquad.club.id;
          r.players[playerKey]["dossierId"] = dossierId; // Track dossier for players
          r.players[playerKey]["playerId"] = p.playerId; // Store the original playerId
        }
      });

      matchReport?.away?.playersStats?.forEach((p) => {
        const playerKey = `${p.playerId}-${dossierId}`; // Composite key for players
        if (r.players[playerKey]) {
          r.players[playerKey]["stats"] = addStats(r.players[playerKey]["stats"], p);
        } else {
          r.players[playerKey] = {};
          r.players[playerKey]["player"] = match.awayFormation.positions
            ?.map((c) => c.player)
            .filter((c) => c.id === p.playerId)
            .pop();
          r.players[playerKey]["stats"] = addStats(
            {
              games: 0,
              minutes: 0,
            },
            p,
          );
          r.players[playerKey]["clubId"] = match.awaySquad.club.id;
          r.players[playerKey]["dossierId"] = dossierId; // Track dossier for players
          r.players[playerKey]["playerId"] = p.playerId; // Store the original playerId
        }
      });
    });

    return r;
  };

  const getSortValue = (stats, key) => {
    switch (key) {
      case "minutes":
        return Math.round(stats?.minutes || 0);

      case "efficiency":
        return (stats?.goals || 0) - (stats?.xG || 0);

      case "shotsOnTargetPct":
        return stats?.shots ? (stats?.shotsOnTarget || 0) / stats.shots : 0;

      case "passesPct":
        return stats?.passes ? (stats?.passesAccurate || 0) / stats.passes : 0;

      case "crossesPct":
        return stats?.crosses ? (stats?.crossesAccurate || 0) / stats.crosses : 0;

      default:
        return stats?.[key] || 0;
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const sortedPlayers = useMemo(() => {
    return Object.entries(currentAggregatedReport?.players || {})
      .filter(([, playerData]) => {
        const stats = playerData.stats || {};

        const games = stats.games || 0;
        const minutes = stats.minutes || 0;

        return games >= minGames && minutes >= minMinutes;
      })
      .sort(([, a], [, b]) => {
        const aStats = a.stats || {};
        const bStats = b.stats || {};

        const aValue =
          sortConfig.key === "minutes" ? Math.round(aStats.minutes || 0) : getSortValue(aStats, sortConfig.key);

        const bValue =
          sortConfig.key === "minutes" ? Math.round(bStats.minutes || 0) : getSortValue(bStats, sortConfig.key);

        if (sortConfig.direction === "asc") {
          return aValue - bValue;
        }

        return bValue - aValue;
      });
  }, [currentAggregatedReport, sortConfig, minGames]);

  const renderHeader = (label, key) => (
    <div
      className={"d-flex justify-content-center d-none d-sm-flex " + (sortConfig.key === key && "text-main fw-bold")}
      style={{
        minWidth: 60,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => handleSort(key)}
    >
      {label}

      {sortConfig.key === key && <span className="ms-1">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>}
    </div>
  );

  const getCellId = (playerKey, statKey) => {
    return `${playerKey}-${statKey}`;
  };

  const isCellSelected = (playerKey, statKey) => {
    return selectedCells.includes(getCellId(playerKey, statKey));
  };

  const toggleCellSelection = (playerKey, statKey) => {
    const id = getCellId(playerKey, statKey);

    setSelectedCells((prev) => {
      if (prev.includes(id)) {
        return prev.filter((c) => c !== id);
      }
      return [...prev, id];
    });
  };

  const renderStat = (playerKey, statKey, value, digits = 2, suffix = "") => (
    <div
      onClick={() => toggleCellSelection(playerKey, statKey)}
      className={
        "d-flex justify-content-center d-none d-sm-flex table-cell " +
        (isCellSelected(playerKey, statKey) ? "primary-color-background text-white" : "")
      }
      style={{
        minWidth: 60,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {formatStat(value, digits, suffix)}
    </div>
  );

  useEffect(() => {
    if (props.assistantUser) {
      const matches = dossiers?.filter((d) => d.id === selectedDossierId).pop()?.matches;

      if (matches) {
        setMatchesToLoad(matches);
      }

      fetchMatchReports();
    }
  }, [selectedDossierId]);

  useEffect(() => {
    if (props.assistantUser) {
      const matches = dossiers?.filter((d) => d.id === selectedDossierId2).pop()?.matches;

      if (matches) {
        setMatchesToLoad(matches);
      }

      fetchMatchReports();
    }
  }, [selectedDossierId2]);

  useEffect(() => {
    fetchMatchReports();
  }, [matchesToLoad]);

  useEffect(() => {
    fetchMatchDossiers();
  }, [props.assistantUser]);

  useEffect(() => {
    if (selectedDossierId || selectedDossierId2) {
      const report1 = selectedDossierId ? computeAggregatedReport(selectedDossierId) : { teams: {}, players: {} };
      const report2 = selectedDossierId2 ? computeAggregatedReport(selectedDossierId2) : { teams: {}, players: {} };

      // Merge reports, ensuring teams from both dossiers are included
      const mergedReport = {
        teams: { ...(report1?.teams || {}), ...(report2?.teams || {}) },
        players: { ...(report1?.players || {}), ...(report2?.players || {}) },
      };

      setCurrentAggregatedReport(mergedReport);
    }
  }, [selectedDossierId, selectedDossierId2, loadedMatches, loadedMatchReports, dossiers, aggregationMethod]);

  if (!props.assistantUser) {
    return (
      <div className="d-flex h-100 justify-content-center align-items-center">
        <ButtonLogin
          flowUser={props.flowUser}
          assistantUser={props.assistantUser}
          logout={props.logout}
          content={<BoxLogin assistantUser={props.assistantUser} />}
        />
      </div>
    );
  }

  return (
    <div id="PageTacticAnalysis" className="h-100 w-100">
      <div className="d-flex flex-column h-100 w-100">
        <nav className="MenuTop navbar">
          <h3 className="my-2 ps-5">
            <i className="bi bi-clipboard2-pulse-fill me-2"></i>
            Tactic analysis
          </h3>
          {isLoading && (
            <div className="d-flex align-items-center gap-2 px-3 primary-color" role="status" aria-live="polite">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Loading matches… ({matchesToLoad.length} remaining)
            </div>
          )}
        </nav>

        <div
          className="position-relative d-flex flex-fill w-100"
          aria-busy={isLoading}
          inert={isLoading ? "" : undefined}
          style={{ minHeight: 0, pointerEvents: isLoading ? "none" : undefined }}
        >
          {isLoading && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex"
              style={{ zIndex: 20 }}
              aria-hidden="true"
            >
              <LoadingSquare height="100%" />
            </div>
          )}
        <div className="container-fluid px-1 px-md-4 py-4" style={{ opacity: isLoading ? 0.55 : 1 }}>
          <div className="d-flex flex-column flex-md-row">
            <div className="flex-shrink-0">
              <BoxCard
                className="fit-content"
                title={"Dossiers"}
                actions={
                  <ControllerMatchDossierAddButton
                    dossiers={dossiers}
                    user={props.assistantUser}
                    afterAdd={fetchMatchDossiers}
                  />
                }
                content={
                  <ControllerMatchDossier
                    hideAddButton
                    dossiers={dossiers}
                    onSelect={(c) => {
                      if (selectedDossierId2 === c) {
                        setSelectedDossierId2(null);
                      }
                      setSelectedDossierId(c !== selectedDossierId ? c : null);
                    }}
                    onSelect2={(c) => {
                      if (selectedDossierId === c) {
                        setSelectedDossierId(null);
                      }
                      setSelectedDossierId2(c !== selectedDossierId2 ? c : null);
                    }}
                    onEditClose={() => computeAggregatedReport()}
                    onDelete={fetchMatchDossiers}
                    user={props.assistantUser}
                    selectedId={selectedDossierId}
                    selectedId2={selectedDossierId2}
                    afterAdd={fetchMatchDossiers}
                    onMove={changeDossierPosition}
                    loadedMatches={loadedMatches}
                    setMatchesToLoad={setMatchesToLoad}
                  />
                }
              />
            </div>

            {(selectedDossierId || selectedDossierId2) && (
              <div className="d-flex flex-column flex-fill">
                <div className="d-flex">
                  <div className="d-flex flex-fill justify-content-end">
                    <ButtonSelect
                      selected={view}
                      onChange={(v) => setView(v)}
                      buttons={{
                        overview: "Overview",
                        players: "Players",
                      }}
                    />
                  </div>
                </div>

                <div key={view} className="match-dossier-view d-flex flex-column">
                {view === "overview" && (
                  <div className="d-flex flex-column">
                    <div className="d-flex flex-column flex-md-row">
                      <BoxCard
                        title={"Games"}
                        content={
                          <div className="mx-1 px-2 pb-2 background-color">
                            {renderTeamsTable(currentAggregatedReport?.teams, ["games"], "games")}
                          </div>
                        }
                      />
                      <BoxCard
                        title={"Results"}
                        content={
                          <div className="mx-1 px-2 pb-2 background-color">
                            {renderTeamsTable(currentAggregatedReport?.teams, ["wins", "draws", "losses"], "wins")}
                          </div>
                        }
                      />
                      {/*<BoxCard title={"Overall"} content={<div>dddddddddd</div>} />*/}
                    </div>
                    <div className="d-flex flex-column flex-md-row flex-fill">
                      <BoxCard
                        className="w-100"
                        title={"Attack"}
                        actions={
                          <ButtonSelect
                            selected={aggregationMethod}
                            onChange={(v) => setAggregationMethod(v)}
                            buttons={{
                              per90: "Per match",
                              total: "Total",
                            }}
                          />
                        }
                        content={
                          <div className="w-100 row g-2">
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Goals</h6>
                                {renderTeamsTable(currentAggregatedReport?.teams, ["goals", "xG"], "goals", [
                                  "Goal",
                                  "xG",
                                ])}
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Shots</h6>
                                {renderTeamsTable(
                                  currentAggregatedReport?.teams,
                                  ["shotsOnTarget", "shots"],
                                  "shotsOnTarget",
                                  ["Sho+", "Sho"],
                                )}
                              </div>
                            </div>
                          </div>
                        }
                      />
                    </div>
                    <div className="d-flex flex-column flex-md-row flex-fill">
                      <BoxCard
                        className="w-100"
                        title={"Creation"}
                        content={
                          <div className="w-100 row g-2">
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Passing</h6>
                                {renderTeamsTable(
                                  currentAggregatedReport?.teams,
                                  ["passesAccurate", "passes"],
                                  "passesAccurate",
                                  ["Pas+", "Pas"],
                                )}
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Crosses</h6>
                                {renderTeamsTable(
                                  currentAggregatedReport?.teams,
                                  ["crossesAccurate", "crosses"],
                                  "crossesAccurate",
                                  ["Crs+", "Crs"],
                                )}
                              </div>
                            </div>
                          </div>
                        }
                      />
                    </div>
                    <div className="d-flex flex-column flex-md-row flex-fill">
                      <BoxCard
                        className="w-100"
                        title={"Defense"}
                        content={
                          <div className="w-100 row g-2">
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Interventions</h6>
                                {renderTeamsTable(
                                  currentAggregatedReport?.teams,
                                  ["clearances", "defensiveDuelsWon", "shotsIntercepted", "saves"],
                                  "clearances",
                                  ["Clr", "Duel+", "ShoInt", "Save"],
                                )}
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="mx-1 px-2 py-2 background-color">
                                <h6>Disciplinary</h6>
                                {renderTeamsTable(
                                  currentAggregatedReport?.teams,
                                  ["foulsCommitted", "foulsSuffered", "yellowCards", "redCards"],
                                  "foulsCommitted",
                                  ["Foul-", "Foul+", "Yca", "Rca"],
                                )}
                              </div>
                            </div>
                          </div>
                        }
                      />
                    </div>
                  </div>
                )}

                {view === "players" && (
                  <BoxCard
                    title={"Attack"}
                    actions={
                      <>
                        <div className="d-flex align-items-center">
                          <span className="ms-2">Min. mins:&nbsp;</span>
                          <input
                            type="number"
                            min={0}
                            value={minMinutes}
                            onChange={(e) => setMinMinutes(Math.max(0, parseInt(e.target.value || 0, 10)))}
                            className="form-control-sm mx-1 my-1"
                            style={{ width: "60px" }}
                          />
                          <span className="ms-2">Min. games:&nbsp;</span>
                          <input
                            type="number"
                            min={0}
                            max={9}
                            value={minGames}
                            onChange={(e) => setMinGames(Math.max(1, parseInt(e.target.value || 1, 10)))}
                            className="form-control-sm mx-1 my-1"
                            style={{ width: "45px" }}
                          />
                        </div>

                        <ButtonSelect
                          selected={aggregationMethod}
                          onChange={(v) => setAggregationMethod(v)}
                          buttons={{
                            per90: "Per 90 mins",
                            total: "Total",
                          }}
                        />

                        <ButtonSelect
                          selected={playerView}
                          onChange={(v) => setPlayerView(v)}
                          buttons={{
                            attack: "Attack",
                            creation: "Creation",
                            defense: "Defense",
                            disciplinary: "Disciplinary",
                          }}
                        />
                      </>
                    }
                    content={
                      <div className="d-flex flex-column flex-fill mt-2">
                        <div className="d-flex flex-row">
                          <div className="flex-grow-1 text-truncate"></div>

                          {playerView === "attack" && (
                            <>
                              {renderHeader("Goals", "goals")}
                              {renderHeader("xG", "xG")}
                              {renderHeader("Eff.", "efficiency")}
                              {renderHeader("Shot+", "shotsOnTarget")}
                              {renderHeader("Shot", "shots")}
                              {renderHeader("%SoT", "shotsOnTargetPct")}
                            </>
                          )}

                          {playerView === "creation" && (
                            <>
                              {renderHeader("Ass.", "assists")}
                              {renderHeader("Dri+", "dribblingSuccess")}
                              {renderHeader("Pas+", "passesAccurate")}
                              {renderHeader("Pas", "passes")}
                              {renderHeader("Pas%", "passesPct")}
                              {renderHeader("Crs+", "crossesAccurate")}
                              {renderHeader("Crs", "crosses")}
                              {renderHeader("Crs%", "crossesPct")}
                            </>
                          )}

                          {playerView === "defense" && (
                            <>
                              {renderHeader("Clr", "clearances")}
                              {renderHeader("Duel+", "defensiveDuelsWon")}
                              {renderHeader("Dri-", "dribbledPast")}
                              {renderHeader("ShoInt", "shotsIntercepted")}
                              {renderHeader("Save", "saves")}
                            </>
                          )}

                          {playerView === "disciplinary" && (
                            <>
                              {renderHeader("Foul+", "foulsCommitted")}
                              {renderHeader("Foul-", "foulsSuffered")}
                              {renderHeader("Yca", "yellowCards")}
                              {renderHeader("Rca", "redCards")}
                            </>
                          )}

                          {renderHeader("Mins", "minutes")}
                          {renderHeader("Games", "games")}
                        </div>

                        {sortedPlayers.map(([playerKey, playerData]) => {
                          const stats = playerData.stats;

                          const isFromDossier2 = playerData.dossierId === selectedDossierId2;

                          return (
                            <div
                              key={playerKey}
                              className={
                                "d-flex flex-row table-row " +
                                (isFromDossier2
                                  ? "light-main-blue-color-background"
                                  : "light-main-pink-color-background")
                              }
                            >
                              <ItemRowPlayer p={playerData.player} clubId={playerData.clubId} />

                              {playerView === "attack" && (
                                <>
                                  {renderStat(playerKey, "goals", stats?.goals, aggregationMethod === "per90" ? 2 : 0)}

                                  {renderStat(playerKey, "xG", stats?.xG, 2)}

                                  {renderStat(playerKey, "efficiency", (stats?.goals || 0) - (stats?.xG || 0), 2)}

                                  {renderStat(
                                    playerKey,
                                    "shotsOnTarget",
                                    stats?.shotsOnTarget,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}

                                  {renderStat(playerKey, "shots", stats?.shots, aggregationMethod === "per90" ? 2 : 0)}

                                  <div
                                    onClick={() => toggleCellSelection(playerKey, "shotsOnTargetPct")}
                                    className={
                                      "d-flex justify-content-center d-none d-sm-flex table-cell " +
                                      (isCellSelected(playerKey, "shotsOnTargetPct")
                                        ? "primary-color-background text-white"
                                        : "")
                                    }
                                    style={{ minWidth: 60, cursor: "pointer", userSelect: "none" }}
                                  >
                                    {formatPercentage(stats?.shotsOnTarget, stats?.shots)}
                                  </div>
                                </>
                              )}

                              {playerView === "creation" && (
                                <>
                                  {renderStat(
                                    playerKey,
                                    "assists",
                                    stats?.assists,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "dribblingSuccess",
                                    stats?.dribblingSuccess,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "passesAccurate",
                                    stats?.passesAccurate,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "passes",
                                    stats?.passes,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}

                                  <div
                                    onClick={() => toggleCellSelection(playerKey, "passesPct")}
                                    className={
                                      "d-flex justify-content-center d-none d-sm-flex table-cell " +
                                      (isCellSelected(playerKey, "passesPct")
                                        ? "primary-color-background text-white"
                                        : "")
                                    }
                                    style={{ minWidth: 60, cursor: "pointer", userSelect: "none" }}
                                  >
                                    {formatPercentage(stats?.passesAccurate, stats?.passes)}
                                  </div>

                                  {renderStat(
                                    playerKey,
                                    "crossesAccurate",
                                    stats?.crossesAccurate,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "crosses",
                                    stats?.crosses,
                                    aggregationMethod === "per90" ? 1 : 0,
                                  )}

                                  <div
                                    onClick={() => toggleCellSelection(playerKey, "crossesPct")}
                                    className={
                                      "d-flex justify-content-center d-none d-sm-flex table-cell " +
                                      (isCellSelected(playerKey, "crossesPct")
                                        ? "primary-color-background text-white"
                                        : "")
                                    }
                                    style={{ minWidth: 60, cursor: "pointer", userSelect: "none" }}
                                  >
                                    {formatPercentage(stats?.crossesAccurate, stats?.crosses)}
                                  </div>
                                </>
                              )}

                              {playerView === "defense" && (
                                <>
                                  {renderStat(
                                    playerKey,
                                    "clearances",
                                    stats?.clearances,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "defensiveDuelsWon",
                                    stats?.defensiveDuelsWon,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "dribbledPast",
                                    stats?.dribbledPast,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "shotsIntercepted",
                                    stats?.shotsIntercepted,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(playerKey, "saves", stats?.saves, aggregationMethod === "per90" ? 2 : 0)}
                                </>
                              )}

                              {playerView === "disciplinary" && (
                                <>
                                  {renderStat(
                                    playerKey,
                                    "foulsCommitted",
                                    stats?.foulsCommitted,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "foulsSuffered",
                                    stats?.foulsSuffered,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "yellowCards",
                                    stats?.yellowCards,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                  {renderStat(
                                    playerKey,
                                    "redCards",
                                    stats?.redCards,
                                    aggregationMethod === "per90" ? 2 : 0,
                                  )}
                                </>
                              )}

                              {renderStat(playerKey, "minutes", stats?.minutes, 0)}

                              {renderStat(playerKey, "games", stats?.games, 0)}
                            </div>
                          );
                        })}
                      </div>
                    }
                  />
                )}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PageToolsTacticAnalysis;
